import type { EvidenceKind } from "contracts/enums/yeet";
import { pluralize } from "../../ui/format";
import {
  readsAsTechnicalValue,
  withoutAgentWrapperTags,
} from "./boundaries/agent-evidence.parser";

type EvidenceValue = {
  kind?: EvidenceKind | null;
  value: string;
};

type PresentedEvidence = {
  readable: EvidenceValue[];
  technical: EvidenceValue[];
};

const TECHNICAL_KINDS: ReadonlySet<EvidenceKind> = new Set(["selector"]);
/** Removes known model-control boundaries from client-facing display text.
 * Callers retain the original value wherever raw technical output is shown. */
export function clientDisplayText(value: string): string {
  return withoutAgentWrapperTags(value);
}

export function presentEvidence(values: EvidenceValue[]): PresentedEvidence {
  const readable: EvidenceValue[] = [];
  const technical: EvidenceValue[] = [];
  for (const item of values) {
    if (isTechnicalEvidence(item)) {
      technical.push(item);
    } else {
      readable.push({ ...item, value: clientDisplayText(item.value) });
    }
  }
  return { readable, technical };
}

export function isTechnicalEvidence(item: EvidenceValue): boolean {
  if (item.kind) {
    return TECHNICAL_KINDS.has(item.kind);
  }
  return readsAsTechnicalValue(item.value);
}

/** A page link's stored evidence, classified: the locator is `selector`-kind
 * (technical); the snippet is readable and caller-gated on site specificity. */
export function linkEvidence(
  link: { locator: string | null; evidenceSnippet: string | null },
  includeSnippet: boolean,
): EvidenceValue[] {
  return [
    ...(includeSnippet && link.evidenceSnippet
      ? [{ value: link.evidenceSnippet }]
      : []),
    ...(link.locator
      ? [{ kind: "selector" as const, value: link.locator }]
      : []),
  ];
}

export function evidenceState(input: {
  readableCount: number;
  technicalCount: number;
  pageCount?: number;
}): string {
  if (input.readableCount > 0) {
    return input.pageCount && input.pageCount > 0
      ? `Evidence available on ${input.pageCount} affected ${pluralize(input.pageCount, "page")}`
      : "Evidence available";
  }
  if (input.technicalCount > 0) {
    return input.pageCount && input.pageCount > 0
      ? "Technical evidence available on the affected pages"
      : "Technical evidence only";
  }
  return "Evidence not stored";
}
